import React, { useState, useEffect } from 'react';
import { X, Download, Star, Building, CheckCircle, Video, Image as ImageIcon, Heart, Calendar, User, MessageSquare, Wand2, Edit3, Send, Sparkles } from 'lucide-react';
import { downloadReviewPdf } from '../../store/vendorApi';

interface Review {
  id: string;
  author: string;
  company: string;
  rating: number;
  comment: string;
  product: string;
  sector: string;
  companySize: string;
  hasVideo: boolean;
  hasImage: boolean;
  date: string;
  featured: boolean;
  verified: boolean;
  helpful: number;
  status: string;
  companyResponse?: string;
}

interface ReviewDetailsModalProps {
  review: Review;
  promoterId: string;
  isOpen: boolean;
  onClose: () => void;
  onUpdateReview: (reviewId: string, updates: Partial<Review>) => void;
}

const ReviewDetailsModal: React.FC<ReviewDetailsModalProps> = ({ review, promoterId, isOpen, onClose, onUpdateReview }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [showResponseSection, setShowResponseSection] = useState(false);
  const [responseMode, setResponseMode] = useState<'ai' | 'manual'>('ai');
  const [responseText, setResponseText] = useState(review.companyResponse || '');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsClosing(false);
      document.body.style.overflow = 'hidden';
    } else if (shouldRender) {
      setIsClosing(true);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsClosing(false);
        document.body.style.overflow = 'unset';
      }, 300);
      return () => clearTimeout(timer);
    }
    
    return () => {
      if (!isOpen) {
        document.body.style.overflow = 'unset';
      }
    };
  }, [isOpen, shouldRender]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 100);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleToggleFeatured = () => {
    onUpdateReview(review.id, { featured: !review.featured });
  };

  const generateAIResponse = async () => {
    setIsGenerating(true);
    
    // Simulate AI generation
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    const aiResponse = `Thank you ${review.author} for this excellent review! We're delighted that ${review.product} has met your expectations and helped improve your operations. Your feedback motivates our team to continue innovating and providing the best possible service. We look forward to continuing our partnership with ${review.company}!`;
    
    setResponseText(aiResponse);
    setIsGenerating(false);
  };

  const handleSaveResponse = () => {
    onUpdateReview(review.id, { companyResponse: responseText });
    setShowResponseSection(false);
  };

  const handleExportPdf = async () => {
    setIsExportingPdf(true);
    setExportError(null);
    try {
      await downloadReviewPdf(
        promoterId,
        review.id,
        `review-${review.author.replace(/\s+/g, '-')}.pdf`
      );
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Export failed. Please try again.');
    } finally {
      setIsExportingPdf(false);
    }
  };

  if (!shouldRender) return null;

  return (
    <>
      <div 
        className={`modal-overlay ${isOpen && !isClosing ? 'open' : ''} ${isClosing ? 'closing' : ''}`} 
        onClick={handleOverlayClick} 
      />
      <div className={`slide-modal w-[800px] max-w-[90vw] ${isOpen && !isClosing ? 'open' : ''} ${isClosing ? 'closing' : ''}`}>
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-white modal-content">
          <h2 className="text-xl font-semibold text-gray-900">Review Details</h2>
          <div className="flex items-center space-x-2">
            <div className="flex flex-col items-end">
              <button
                onClick={handleExportPdf}
                disabled={isExportingPdf}
                className="flex items-center space-x-1 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
              >
                {isExportingPdf ? (
                  <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                <span>{isExportingPdf ? 'Exporting...' : 'Export PDF'}</span>
              </button>
              {exportError && (
                <p className="text-xs text-red-600 mt-0.5">{exportError}</p>
              )}
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>
        
        <div className="flex-1 p-6 overflow-y-auto bg-white modal-content space-y-6" style={{ maxHeight: 'calc(100vh - 80px)', overflowY: 'auto' }}>
          {/* Review Header */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
            <div className="flex items-start space-x-4">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-lg">
                {review.author.split(' ').map(n => n[0]).join('')}
              </div>
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <h3 className="text-xl font-bold text-gray-900">{review.author}</h3>
                  {review.verified && (
                    <CheckCircle className="w-5 h-5 text-green-500" title="Verified" />
                  )}
                  {review.featured && (
                    <Star className="w-5 h-5 text-yellow-500 fill-current" title="Featured" />
                  )}
                </div>
                <p className="text-gray-600 flex items-center mb-2">
                  <Building className="w-4 h-4 mr-1" />
                  {review.company} • {review.companySize}
                </p>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-1">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                    <span className="text-sm text-gray-600 ml-1">({review.rating}/5)</span>
                  </div>
                  <span className="text-sm text-gray-500">
                    {new Date(review.date).toLocaleDateString('en-US')}
                  </span>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {review.hasVideo && (
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Video className="w-5 h-5 text-blue-600" />
                  </div>
                )}
                {review.hasImage && (
                  <div className="p-2 bg-green-100 rounded-lg">
                    <ImageIcon className="w-5 h-5 text-green-600" />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Review Content */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h4 className="font-semibold text-gray-900 mb-4">Review Content</h4>
            <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-blue-400">
              <p className="text-gray-800 italic text-lg leading-relaxed">"{review.comment}"</p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
              <div>
                <span className="font-medium text-gray-600">Product:</span>
                <p className="text-gray-900">{review.product}</p>
              </div>
              <div>
                <span className="font-medium text-gray-600">Sector:</span>
                <p className="text-gray-900">{review.sector}</p>
              </div>
              <div>
                <span className="font-medium text-gray-600">Status:</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  review.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {review.status === 'approved' ? 'Approved' : 'Pending'}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Helpful:</span>
                <div className="flex items-center space-x-1">
                  <Heart className="w-4 h-4 text-red-500" />
                  <span className="text-gray-900">{review.helpful}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Company Response */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-gray-900">Company Response</h4>
              <button
                onClick={() => setShowResponseSection(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <MessageSquare className="w-4 h-4" />
                <span>{review.companyResponse ? 'Edit Response' : 'Add Response'}</span>
              </button>
            </div>
            
            {review.companyResponse ? (
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                <div className="flex items-center space-x-2 mb-2">
                  <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                    <Building className="w-3 h-3 text-white" />
                  </div>
                  <span className="font-medium text-blue-900">Avicenna Response</span>
                </div>
                <p className="text-blue-800">{review.companyResponse}</p>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No response yet</p>
                <p className="text-sm">Click "Add Response" to reply to this review</p>
              </div>
            )}

            {/* Response Section */}
            {showResponseSection && (
              <div className="mt-6 border-t border-gray-200 pt-6">
                <h5 className="text-lg font-semibold text-gray-900 mb-4">
                  {review.companyResponse ? 'Edit Response' : 'Add Response'}
                </h5>
                
                {/* Response Mode Selection */}
                <div className="mb-6">
                  <h6 className="font-medium text-gray-900 mb-3">Response Mode</h6>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setResponseMode('ai')}
                      className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                        responseMode === 'ai'
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-purple-300'
                      }`}
                    >
                      <div className="text-center">
                        <Wand2 className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                        <h5 className="font-medium text-gray-900">AI Generated</h5>
                        <p className="text-xs text-gray-600">Let AI create a professional response</p>
                      </div>
                    </button>

                    <button
                      onClick={() => setResponseMode('manual')}
                      className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                        responseMode === 'manual'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <div className="text-center">
                        <Edit3 className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                        <h5 className="font-medium text-gray-900">Manual</h5>
                        <p className="text-xs text-gray-600">Write your own response</p>
                      </div>
                    </button>
                  </div>
                </div>

                {/* AI Generation */}
                {responseMode === 'ai' && (
                  <div className="mb-6">
                    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-4 border border-purple-200">
                      <div className="flex items-center space-x-3 mb-3">
                        <Sparkles className="w-5 h-5 text-purple-600" />
                        <h5 className="font-medium text-purple-900">AI Response Generator</h5>
                      </div>
                      <p className="text-sm text-purple-800 mb-4">
                        Our AI will generate a professional and personalized response based on the review content.
                      </p>
                      <button
                        onClick={generateAIResponse}
                        disabled={isGenerating}
                        className="w-full bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                      >
                        {isGenerating ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            <span>Generating...</span>
                          </>
                        ) : (
                          <>
                            <Wand2 className="w-4 h-4" />
                            <span>Generate Response</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {/* Response Text Area */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Response Text
                  </label>
                  <textarea
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                    rows={8}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder={responseMode === 'ai' 
                      ? 'Click "Generate Response" to create an AI response...'
                      : 'Write your response to this review...'
                    }
                  />
                </div>

                {/* Preview */}
                {responseText && (
                  <div className="mb-6">
                    <h6 className="font-medium text-gray-900 mb-2">Preview</h6>
                    <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                          <Building className="w-3 h-3 text-white" />
                        </div>
                        <span className="font-medium text-blue-900">Avicenna Response Preview</span>
                      </div>
                      <p className="text-blue-800 text-sm">{responseText}</p>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowResponseSection(false)}
                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveResponse}
                    disabled={!responseText.trim()}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    <Send className="w-4 h-4" />
                    <span>Save Response</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h4 className="font-semibold text-gray-900 mb-4">Actions</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => setShowResponseSection(true)}
                className="flex items-center justify-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <MessageSquare className="w-5 h-5" />
                <span>{review.companyResponse ? 'Edit Response' : 'Respond to Review'}</span>
              </button>
              
              <button
                onClick={handleToggleFeatured}
                className={`flex items-center justify-center space-x-2 px-6 py-3 rounded-lg transition-colors ${
                  review.featured
                    ? 'bg-yellow-100 text-yellow-800 border border-yellow-300 hover:bg-yellow-200'
                    : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                }`}
              >
                <Star className="w-5 h-5" />
                <span>{review.featured ? 'Remove from Featured' : 'Make Featured'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReviewDetailsModal;